/**
 * useGhcpOAuthConnection Hook
 *
 * Creates app-scoped GitHub service-provider connections for GitHub Copilot
 * agent calls using the portal's shared OAuth flow.
 *
 * The user sees "GitHub Copilot" as the provider label; the service-provider
 * resource ID remains an implementation detail.
 */

import { useOAuthConnection, type OAuthConnectionResult, type OAuthFlowState } from '@/hooks/useOAuthConnection';
import type { ConnectionResource, TestConnectionResult } from '@/services/connectionApi';
import { GITHUB_SERVICE_PROVIDER_CONNECTOR_ID } from '@/utils/connections';

// =============================================================================
// TYPES
// =============================================================================

export interface GhcpOAuthParams {
  /** Display name for the connection (user-chosen) */
  connectionName: string;
}

export interface UseGhcpOAuthConnectionReturn {
  /** Current OAuth flow state */
  state: OAuthFlowState;
  /** Error message if flow failed */
  error: string | null;
  /** Result on success */
  result: GhcpOAuthResult | null;
  /** Start the GitHub OAuth consent flow */
  connect: (params: GhcpOAuthParams) => Promise<OAuthConnectionResult>;
  /** Cancel in-progress flow */
  cancel: () => void;
  /** Reset to idle */
  reset: () => void;
  /** Delete an abandoned, still-uncommitted successful connection, then reset */
  discard: () => Promise<void>;
  /** Whether a flow is in progress */
  isInProgress: boolean;
}
export interface GhcpOAuthResult {
  connection: ConnectionResource;
  testResult: TestConnectionResult | null;
}


// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for creating GitHub Copilot agent connections via OAuth.
 * Creates the app-scoped credential connection used by the local agent reference.
 */
export function useGhcpOAuthConnection(): UseGhcpOAuthConnectionReturn {
  const oauth = useOAuthConnection();

  const connect = (params: GhcpOAuthParams): Promise<OAuthConnectionResult> =>
    oauth.createOAuthConnection({
      connectionName: params.connectionName,
      connectorId: GITHUB_SERVICE_PROVIDER_CONNECTOR_ID,
      parameterValues: {},
    });

  // Only expose a result once the flow reaches its terminal success state.
  // `useOAuthConnection` sets `connection` right after resource creation — before
  // consent completes — so gating on the connection alone would surface a
  // partially-authenticated connection to consumers.
  const result = oauth.state === 'success' && oauth.connection
    ? { connection: oauth.connection, testResult: oauth.testResult }
    : null;

  return {
    state: oauth.state,
    error: oauth.error,
    result,
    connect,
    cancel: oauth.cancel,
    reset: oauth.reset,
    discard: oauth.discard,
    isInProgress: oauth.isInProgress,
  };
}
